import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { resetPassword } from "../../../data";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthLayout } from "../shared/layout";
import { useToast } from "@/components/ui/use-toast";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";

export function PasswordResetScreen() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();
  const password = watch("password");

  const {
    mutate: resetPasswordMutation,
    isLoading,
  } = useMutation({
    mutationKey: "resetPassword",
    mutationFn: (data) => resetPassword({ ...data, token }),
    onSuccess: () => {
      toast({
        title: t("common.success"),
        description: t("auth.passwordResetSuccess"),
        variant: "success",
      });
      navigate("/login");
    },
    onError: (error) => {
      toast({
        title: t("common.error"),
        description: error.message || t("auth.passwordResetFailed"),
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data) => {
    resetPasswordMutation(data);
  };

  return (
    <AuthLayout 
      title={t("auth.resetPasswordTitle")} 
      description={t("auth.resetPasswordSubtitle")}
      backButton={
        <Button variant="link" className="px-0 font-normal" asChild>
          <Link to="/login">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("auth.backToLogin")}
          </Link>
        </Button>
      }
    >
      <div className="grid gap-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="password">{t("auth.newPassword")}</Label>
              <div className="relative">
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: t("auth.newPasswordRequired"),
                    minLength: {
                      value: 8,
                      message: t("auth.passwordMinLength"),
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="password"
                      type={showPassword ? "text" : "password"}
                      disabled={isLoading}
                    />
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {errors.password && <p className="text-sm text-red-500">{errors.password.message}</p>}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="confirmPassword">{t("auth.confirmPassword")}</Label>
              <Controller
                name="confirmPassword"
                control={control}
                rules={{
                  required: t("auth.confirmPasswordRequired"),
                  validate: (value) =>
                    value === password || t("auth.passwordsDoNotMatch"),
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    disabled={isLoading}
                  />
                )}
              />
              {errors.confirmPassword && <p className="text-sm text-red-500">{errors.confirmPassword.message}</p>}
            </div>
            <Button disabled={isLoading}>
              {isLoading && (
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              )}
              {t("auth.resetPassword")}
            </Button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
}
